import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    fetchDoctorClinicalNotePreference,
    resetDoctorClinicalNotePreference,
    updateDoctorClinicalNotePreference,
} from "../../../api/services/clinic/doctor";
import Button from "../../../components/Button";
import Checkbox from "../../../components/Checkbox";
import InputField from "../../../components/InputField";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { useToast } from "../../../contexts/ToastContext";
import { useRecorderStable } from "../presscribe/RecorderContext";

const SUMMARY_STYLE_OPTIONS = [
    ["style_1", "แบบย่อ"],
    ["style_2", "ทั่วไป"],
];

const SUMMARY_LANGUAGE_OPTIONS = [
    ["th", "ภาษาไทย"],
    ["en", "English"],
];

const DEFAULT_SUMMARY_SETTINGS = {
    summaryStyle: SUMMARY_STYLE_OPTIONS[0][0],
    summaryLanguage: SUMMARY_LANGUAGE_OPTIONS[0][0],
    autoSuggestIcd10: false,
    autoSuggestMedication: false,
};

function buildSummarySettings(initialValues = {}) {
    return {
        ...DEFAULT_SUMMARY_SETTINGS,
        ...initialValues,
    };
}

function mapDoctorClinicalNotePreferenceToSettings(preference = {}, currentSettings = {}) {
    return buildSummarySettings({
        ...currentSettings,
        summaryStyle: preference.style,
        summaryLanguage: preference.language,
        autoSuggestIcd10: preference.enable_icd10_suggest,
    });
}

function buildSummaryStyleInfoText(summaryStyle) {
    if (summaryStyle === "style_1") {
        return "สรุปแบบย่อ กระชับ อ่านเร็ว";
    }

    return "ใช้ภาษาสื่อสารทั่วไป เข้าใจง่าย ละเอียด";
}

function mapSettingsToDoctorClinicalNotePreference(settings) {
    return {
        style: settings.summaryStyle,
        language: settings.summaryLanguage,
        enable_icd10_suggest: settings.autoSuggestIcd10,
    };
}

/**
 * SummarySettingsModal
 *
 * Settings modal for configuring summary/prescribe output in visiting management.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {function} [props.onSave]
 * @param {function} [props.onReset]
 * @param {object} [props.initialValues]
 * @param {boolean} [props.editMode]
 * @param {string|number} [props.treatmentId]
 */
function SummarySettingsModalContent({
    onClose,
    onReset,
    onSave,
    onSettingsChange,
    settings,
    saveAsDefaultForNextCase,
    onSaveAsDefaultForNextCaseChange,
    editMode = false,
    isSubmitting = false,
}) {
    function handleFieldChange(event) {
        const { name, value } = event.target;
        onSettingsChange((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function handleToggleChange(field, value) {
        onSettingsChange((prev) => ({
            ...prev,
            [field]: value,
        }));
    }

    function handleReset() {
        onReset?.();
    }

    function buildSavePayload() {
        return (
            editMode
                ? {
                    ...settings,
                    saveAsDefaultForNextCase,
                }
                : settings
        );
    }

    function handleSave() {
        onSave?.(buildSavePayload());
    }

    function buildModalTitle() {
        return editMode ? "ปรับแต่งสรุป" : "ตั้งค่าสรุปบันทึก";
    }

    function buildSummarySettingToggle({
        id,
        label,
        checked,
        disabled = false,
        onChange,
    }) {
        const labelClassName = disabled
            ? "text-main-typography-system-caption"
            : "text-main-typography-system-default";

        const switchClassName = disabled
            ? checked
                ? "bg-creation-primary-solid-surface-disabled cursor-not-allowed"
                : "bg-creation-neutral-solid-surface-disabled cursor-not-allowed"
            : checked
                ? "bg-creation-primary-solid-surface-default"
                : "bg-creation-neutral-solid-surface-default";

        const knobClassName = checked ? "translate-x-6" : "translate-x-0";
        const knobSurfaceClassName = disabled
            ? checked
                ? "bg-creation-primary-container-surface-disabled"
                : "bg-creation-neutral-container-surface-disabled"
            : "bg-main-background-system-default";

        // TODO(taati): Use component from design system when it's ready issue https://code.loolootech.com/loolootech/kindmore/issues/285
        return (
            <div className="flex items-center justify-between gap-4">
                <p className={`font-header text-button-base ${labelClassName}`}>
                    {label}
                </p>
                <button
                    id={id}
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-label={label}
                    disabled={disabled}
                    onClick={() => {
                        if (!disabled) {
                            onChange(!checked);
                        }
                    }}
                    className={`relative inline-flex h-[32px] w-[56px] items-center rounded-full p-1 transition-colors ${switchClassName}`}
                >
                    <span
                        className={`h-[24px] w-[24px] rounded-full transition-transform ${knobClassName} ${knobSurfaceClassName}`}
                    />
                </button>
            </div>
        );
    }

    function buildHeader() {
        return (
            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <p
                        id="summary-settings-title"
                        className="font-header text-title text-main-typography-system-default"
                    >
                        {buildModalTitle()}
                    </p>
                </div>
                <Button
                    hierarchy="tertiary"
                    size="md"
                    icon="x"
                    aria-label={`ปิดหน้าต่าง${buildModalTitle()}`}
                    onClick={onClose}
                />
            </div>
        );
    }

    function buildGeneralSettingsSection() {
        return (
            <div className="flex flex-col gap-5">
                <p className="text-body-base text-main-typography-system-description">
                    ตั้งค่าทั่วไป
                </p>
                <div className="flex flex-col gap-3">
                    <div className="flex justify-between">
                        <p className="font-header text-button-base text-main-typography-system-default">
                            สไตล์ของสรุป
                        </p>
                        <InputField
                            name="summaryStyle"
                            type="select"
                            value={settings.summaryStyle}
                            choices={SUMMARY_STYLE_OPTIONS}
                            dropdownExtraClasses="w-[300px]"
                            infoText={buildSummaryStyleInfoText(settings.summaryStyle)}
                            onChange={handleFieldChange}
                        />
                    </div>
                    <div className="flex justify-between">
                        <p className="font-header text-button-base text-main-typography-system-default">
                            ภาษาของสรุป
                        </p>
                        <InputField
                            name="summaryLanguage"
                            type="select"
                            value={settings.summaryLanguage}
                            choices={SUMMARY_LANGUAGE_OPTIONS}
                            dropdownExtraClasses="w-[300px]"
                            onChange={handleFieldChange}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function buildClinicalNoteSection() {
        return (
            <div className="flex flex-col gap-6">
                <p className="text-body-base text-main-typography-system-description">
                    Clinical note
                </p>

                {buildSummarySettingToggle({
                    id: "autoSuggestIcd10",
                    label: "แนะนำ ICD10 อัตโนมัติ",
                    checked: settings.autoSuggestIcd10,
                    onChange: (value) => handleToggleChange("autoSuggestIcd10", value),
                })}

                {buildSummarySettingToggle({
                    id: "autoSuggestMedication",
                    label: "แนะนำยา",
                    checked: settings.autoSuggestMedication,
                    // TODO(taati): Enable this when the drug recommendation feature is ready.
                    disabled: true,
                    onChange: (value) => handleToggleChange("autoSuggestMedication", value),
                })}
            </div>
        );
    }

    function buildActionSection() {
        if (editMode) {
            return (
                <div className="flex items-center justify-between">
                    <Checkbox
                        id="save-as-default-for-next-case"
                        label="บันทึกเป็นค่าเริ่มต้นในเคสถัดไป"
                        checked={saveAsDefaultForNextCase}
                        onChange={(event) => onSaveAsDefaultForNextCaseChange(event.target.checked)}
                    />
                    <Button
                        id="save-and-apply-summary-settings"
                        hierarchy="primary"
                        size="md"
                        text="บันทึกและนำไปใช้"
                        className="w-[160px]"
                        disabled={isSubmitting}
                        onClick={handleSave}
                    />
                </div>
            );
        }

        return (
            <div className="flex justify-between">
                <Button
                    id="reset-summary-settings"
                    hierarchy="secondary"
                    size="md"
                    text="คืนค่าเริ่มต้น"
                    className="w-[120px]"
                    disabled={isSubmitting}
                    onClick={handleReset}
                />
                <Button
                    id="save-summary-settings"
                    hierarchy="primary"
                    size="md"
                    text="บันทึก"
                    className="w-[160px]"
                    disabled={isSubmitting}
                    onClick={handleSave}
                />
            </div>
        );
    }

    function buildDivider() {
        return <div className="h-px w-full bg-main-border-system-default" />;
    }

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="summary-settings-title"
            className="flex h-fit w-[560px] flex-col gap-6 rounded-xl bg-main-background-system-default p-6"
        >
            {buildHeader()}
            {buildGeneralSettingsSection()}
            {!editMode && buildDivider()}
            {!editMode && buildClinicalNoteSection()}
            {buildDivider()}
            {buildActionSection()}
        </div>
    );
}

export default function SummarySettingsModal({
    isOpen,
    onClose,
    onSave,
    onReset,
    initialValues,
    editMode = false,
    treatmentId,
}) {
    const resolvedInitialValues = buildSummarySettings(initialValues);
    const resolvedInitialValuesKey = JSON.stringify(resolvedInitialValues);
    const shouldLoadPreference = isOpen && !editMode;
    const [settings, setSettings] = useState(resolvedInitialValues);
    const [saveAsDefaultForNextCase, setSaveAsDefaultForNextCase] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingPreference, setIsLoadingPreference] = useState(false);
    const preferenceCacheRef = useRef(null);
    const { showErrorToast, showSuccessToast } = useToast();
    const { summarizeBySettings } = useRecorderStable();

    useLayoutEffect(() => {
        const cachedPreference = preferenceCacheRef.current;
        const nextSettings = (
            editMode
                ? resolvedInitialValues
                : shouldLoadPreference && cachedPreference
                ? mapDoctorClinicalNotePreferenceToSettings(cachedPreference, resolvedInitialValues)
                : resolvedInitialValues
        );

        setSettings(nextSettings);
        setSaveAsDefaultForNextCase(false);
        setIsSubmitting(false);
        setIsLoadingPreference(shouldLoadPreference && !cachedPreference);
    }, [editMode, resolvedInitialValuesKey, shouldLoadPreference]);

    useEffect(() => {
        if (!shouldLoadPreference) {
            return undefined;
        }

        let isCurrentRequest = true;

        async function loadDoctorClinicalNotePreference() {
            try {
                if (!preferenceCacheRef.current) {
                    setIsLoadingPreference(true);
                }
                const preference = await fetchDoctorClinicalNotePreference();

                if (!isCurrentRequest) {
                    return;
                }

                preferenceCacheRef.current = preference;
                setSettings((currentSettings) => (
                    mapDoctorClinicalNotePreferenceToSettings(preference, currentSettings)
                ));
            } catch {
                // Keep fallback values when the preference endpoint is unavailable.
            } finally {
                if (isCurrentRequest) {
                    setIsLoadingPreference(false);
                }
            }
        }

        loadDoctorClinicalNotePreference();

        return () => {
            isCurrentRequest = false;
        };
    }, [resolvedInitialValuesKey, shouldLoadPreference]);

    function handleSettingsChange(nextSettings) {
        if (typeof nextSettings === "function") {
            setSettings(nextSettings);
            return;
        }

        setSettings(nextSettings);
        onReset?.(nextSettings);
    }

    async function handleSave(payload) {
        try {
            setIsSubmitting(true);

            if (!editMode) {
                await updateDoctorClinicalNotePreference(
                    mapSettingsToDoctorClinicalNotePreference(payload)
                );
                onSave?.(payload);
                onClose?.();
                return;
            }

            onSave?.(payload);
            onClose?.();

            if (treatmentId) {
                summarizeBySettings(treatmentId, {
                    style: payload.summaryStyle,
                    language: payload.summaryLanguage,
                    saveAsDefault: payload.saveAsDefaultForNextCase,
                });
            }
        } catch (error) {
            showErrorToast("ไม่สามารถบันทึกการตั้งค่าสรุปได้", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleReset() {
        try {
            setIsSubmitting(true);
            await resetDoctorClinicalNotePreference();
            const optimisticSettings = buildSummarySettings();
            setSettings(optimisticSettings);

            let nextSettings = optimisticSettings;
            try {
                const preference = await fetchDoctorClinicalNotePreference();
                preferenceCacheRef.current = preference;
                nextSettings = mapDoctorClinicalNotePreferenceToSettings(preference, nextSettings);
            } catch {
                // Keep fallback values when the preference endpoint is unavailable.
            }

            setSettings(nextSettings);
            onReset?.(nextSettings);
            showSuccessToast("คืนค่าเริ่มต้นสำเร็จ");
        } catch (error) {
            showErrorToast("ไม่สามารถคืนค่าเริ่มต้นของสรุปได้", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    if (!isOpen) return null;

    if (isLoadingPreference) {
        return (
            <div className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 px-6 py-8 modal-scroll">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="กำลังโหลดการตั้งค่า"
                    className="flex h-fit w-[560px] items-center justify-center rounded-xl bg-main-background-system-default p-6"
                >
                    <LoadingOverlay overlayType="loading" message="กำลังโหลดการตั้งค่า..." />
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 px-6 py-8 modal-scroll">
            <SummarySettingsModalContent
                onClose={onClose}
                onReset={handleReset}
                onSave={handleSave}
                onSettingsChange={handleSettingsChange}
                settings={settings}
                saveAsDefaultForNextCase={saveAsDefaultForNextCase}
                onSaveAsDefaultForNextCaseChange={setSaveAsDefaultForNextCase}
                editMode={editMode}
                isSubmitting={isSubmitting}
            />
        </div>
    );
}
